import { useState } from 'react';
import { Alert, Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { validateColumnName } from '../utils/spanCoverage';

interface NewAreaModalProps {
  industry: string;
  saving: boolean;
  onClose: () => void;
  /** Resolves `null` on success (backend writes a placeholder UC alongside the new
   *  Area), or a ready-to-show failure message. */
  onSave: (input: { areaName: string; placeholderUcName: string }) => Promise<string | null>;
}

export default function NewAreaModal({
  industry,
  saving,
  onClose,
  onSave,
}: NewAreaModalProps) {
  const [areaName, setAreaName] = useState('');
  const [ucName, setUcName] = useState('(新 UC)');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const a = areaName.trim();
    const u = ucName.trim();
    if (!a) {
      setError('Area 名称不能为空');
      return;
    }
    if (!u) {
      setError('占位 UC 名称不能为空');
      return;
    }
    // This modal had no name validation at all, so a column literally named `_span_` was
    // creatable. See validateColumnName for the two names that break the data model.
    const invalid = validateColumnName(a);
    if (invalid) {
      setError(invalid);
      return;
    }
    const failure = await onSave({ areaName: a, placeholderUcName: u });
    if (failure) setError(failure);
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!saving) onClose();
      }}
      header={`新建 Solution Area（${industry}）`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={!areaName.trim() || !ucName.trim()}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="small">新增一列；后端要求至少有一个 UC，先填占位 UC 名（之后可改）</Box>

        <FormField label="Solution Area Name *">
          <Input
            value={areaName}
            onChange={({ detail }) => setAreaName(detail.value)}
            disabled={saving}
            autoFocus
            placeholder="e.g. Engagement"
          />
        </FormField>

        <FormField label="占位 UC 名称 *">
          <Input
            value={ucName}
            onChange={({ detail }) => setUcName(detail.value)}
            disabled={saving}
          />
        </FormField>

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
